/**
 * The platform's public status (packages/api/src/routes/status.ts): whether the deployment is up,
 * each part of it, and the incidents its administrators have written. Public and without sign-in,
 * so it carries no tenant data and no secrets: component states come from the readiness checks,
 * run on a cadence and cached (every replica answers the same through Redis), and incidents are
 * deployment-wide.
 *
 *   GET   /status                                   a small HTML page, refreshing every 60 s
 *   GET   /status/v1                                StatusSummary
 *   GET   /admin/v1/status/incidents                StatusIncidentList (every incident, newest first; administrators)
 *   POST  /admin/v1/status/incidents                StatusIncidentCreate -> StatusIncident
 *   PATCH /admin/v1/status/incidents/:incidentId    StatusIncidentPatch -> StatusIncident
 *
 * Public addresses: https://service.mojoup.ai/status (Mojo Up AI Cloud) and
 * https://aioe.mojoup.com.au/status (Mojo Up's own Enterprise deployment); every self-hosted
 * deployment serves the same at its API host.
 */
import { z } from "zod";
export const STATUS_SCHEMA_VERSION = 1;
export const STATUS_PATH = "/status/v1";
export const STATUS_PAGE_PATH = "/status";
export const STATUS_INCIDENTS_PATH = "/admin/v1/status/incidents";
/** How long a check's result stands before it is run again (and how often the page refreshes, twice that). */
export const STATUS_CHECK_SECONDS = 30;
/** Resolved incidents stay on the public page this many days. */
export const STATUS_RESOLVED_DAYS = 30;
/** `ok` working; `degraded` working but slow or partly; `down` not working. */
export const statusStateSchema = z.enum(["ok", "degraded", "down"]);
/** The parts of a deployment the page reports. `mesh` is the Enterprise edition's private network. */
export const statusComponentIdSchema = z.enum(["api", "database", "redis", "relay", "mesh", "releases"]);
/** Each component's name in words, for the page and the console. */
export const STATUS_COMPONENT_NAMES = {
    api: "Service",
    database: "Database",
    redis: "Shared cache",
    relay: "Remote control relay",
    mesh: "Private network",
    releases: "Releases feed",
};
/** One component as last checked. `message` is plain words, never an error's own text (which may name hosts). */
export const statusComponentSchema = z.object({
    id: statusComponentIdSchema,
    name: z.string(),
    state: statusStateSchema,
    message: z.string(),
    checkedAt: z.string(),
});
/** `minor`: some people notice; `major`: a part does not work for many; `outage`: the platform does not work. */
export const statusImpactSchema = z.enum(["minor", "major", "outage"]);
export const STATUS_IMPACT_WORDS = {
    minor: "Minor",
    major: "Major",
    outage: "Outage",
};
/** What an incident's impact does to the components it names, and to the platform as a whole. */
export const statusImpactState = (impact) => (impact === "outage" ? "down" : "degraded");
/** The worse of two states. */
export function worseState(a, b) {
    const rank = { ok: 0, degraded: 1, down: 2 };
    return rank[a] >= rank[b] ? a : b;
}
const text = (max) => z.string().trim().min(1).max(max);
/** One step of an incident's timeline. */
export const statusIncidentUpdateSchema = z.object({
    at: z.string(),
    text: text(2000),
});
export const statusIncidentSchema = z.object({
    id: z.string(),
    title: text(200),
    impact: statusImpactSchema,
    components: z.array(statusComponentIdSchema).max(6),
    /** Oldest first. */
    updates: z.array(statusIncidentUpdateSchema),
    startedAt: z.string(),
    resolvedAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const isoTime = z.string().refine((s) => !Number.isNaN(Date.parse(s)), "a date and time (ISO 8601)");
/** Opens an incident: what is wrong, how badly, what it touches, and the first word on it. */
export const statusIncidentCreateSchema = z.object({
    title: text(200),
    impact: statusImpactSchema,
    components: z.array(statusComponentIdSchema).max(6).default([]),
    text: text(2000),
    /** When it began; now when left out. */
    startedAt: isoTime.optional(),
});
/**
 * Changes an incident. `update` adds a step to the timeline (now, unless `at` is given);
 * `resolvedAt` resolves it (a time) or opens it again (null).
 */
export const statusIncidentPatchSchema = z.object({
    title: text(200).optional(),
    impact: statusImpactSchema.optional(),
    components: z.array(statusComponentIdSchema).max(6).optional(),
    update: z.object({ text: text(2000), at: isoTime.optional() }).optional(),
    resolvedAt: isoTime.nullable().optional(),
});
/** The console's list: every incident, newest first, and whether this administrator may write them (and why not). */
export const statusIncidentListSchema = z.object({
    incidents: z.array(statusIncidentSchema),
    mayWrite: z.boolean(),
    why: z.string().optional(),
});
/** What `GET /status/v1` answers. */
export const statusSummarySchema = z.object({
    schemaVersion: z.literal(STATUS_SCHEMA_VERSION),
    overall: statusStateSchema,
    /** When the components were last checked; absent before the first check. */
    checkedAt: z.string().optional(),
    components: z.array(statusComponentSchema),
    incidents: z.object({
        open: z.array(statusIncidentSchema),
        /** Resolved in the last STATUS_RESOLVED_DAYS days, newest first. */
        resolved: z.array(statusIncidentSchema),
    }),
});
/** The overall state in words, for the page's banner. */
export const STATUS_OVERALL_WORDS = {
    ok: "All systems working",
    degraded: "Some systems are degraded",
    down: "Major outage",
};
//# sourceMappingURL=status.js.map